'use client';

import React from 'react';
import Image from 'next/image';
import { m, useReducedMotion } from 'motion/react';
import { css, cx } from 'styled-system/css';

import type { ShowcaseVisual as Visual } from '@shaojiang/data/site';

/**
 * A small live preview of a showcase, drawn with CSS and Motion instead of a screenshot:
 * equalizer bars, a turning conic ring, flowing dashes, a sketch of a form or a page.
 * Paints with `currentColor` in Storybook (on a colored block) and with glowing accents in Nebula.
 */
const frame = css({
  position: 'relative',
  overflow: 'hidden',
  display: 'grid',
  placeItems: 'center',
  aspectRatio: '4 / 3',
  borderRadius: 'media',
  bg: 'rgba(255, 255, 255, .45)',
  _dark: { bg: 'rgba(255, 255, 255, .08)' },
  _nebula: {
    borderRadius: 'calc({radii.card} - 8px)',
    bg: 'radial-gradient(80% 80% at 50% 0%, {colors.brand.subtle}, transparent), {colors.bg.subtle}',
    _dark: {
      bg: 'radial-gradient(80% 80% at 50% 0%, {colors.brand.subtle}, transparent), {colors.bg.subtle}',
    },
  },
  '& img': { objectFit: 'cover', transition: 'scale {durations.long} {easings.out}' },
  _groupHover: { '& img': { scale: '1.05' } },
});

const bar = css({
  display: 'block',
  w: '2.5',
  borderRadius: '6px',
  bg: 'currentColor',
  _nebula: {
    w: '1.5',
    borderRadius: '3px',
    bg: 'linear-gradient({colors.violet.400}, {colors.aurora.magenta})',
    boxShadow: '0 0 12px {colors.glow}',
    '&:nth-child(3n)': { bg: 'linear-gradient({colors.aurora.cyan}, {colors.violet.400})' },
  },
});

const ring = css({
  position: 'absolute',
  inset: '22%',
  borderRadius: 'card',
  p: '6px',
  bg: 'conic-gradient({colors.tomato.500}, {colors.sun.400}, {colors.leaf.500}, {colors.cobalt.500}, {colors.tomato.500})',
  WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
  WebkitMaskComposite: 'xor',
  maskComposite: 'exclude',
  _nebula: {
    p: '2px',
    bg: 'conic-gradient({colors.violet.500}, {colors.aurora.magenta}, {colors.aurora.cyan}, {colors.violet.500})',
    filter: 'drop-shadow(0 0 8px {colors.glow})',
  },
});

const dash = css({
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: '5',
  strokeLinecap: 'round',
  strokeDasharray: '2 14',
  _nebula: {
    stroke: 'violet.400',
    strokeWidth: '2.5',
    strokeDasharray: '6 8',
    filter: 'drop-shadow(0 0 4px {colors.glow})',
  },
});

const formRow = css({
  display: 'block',
  h: '18px',
  borderRadius: '9px',
  bg: 'rgba(255, 255, 255, .75)',
  '&:first-child': { w: '40%', h: '10px', bg: 'currentColor', opacity: '0.55' },
  '&:last-child': { w: '50%', h: '26px', borderRadius: '13px', bg: 'tomato.500' },
  _nebula: {
    h: '14px',
    borderRadius: '6px',
    bg: 'bg.glass',
    boxShadow: 'inset 0 0 0 1px {colors.border.strong}',
    '&:first-child': { w: '40%', h: '8px', bg: 'fg.subtle', opacity: '1', boxShadow: 'none' },
    '&:last-child': {
      w: '46%',
      h: '22px',
      borderRadius: '8px',
      bgGradient: 'brand',
      boxShadow: '0 6px 18px -6px {colors.glow}',
    },
  },
});

const siteBox = css({
  display: 'grid',
  gridTemplateRows: '16px 1fr 34px',
  gap: '2',
  w: '72%',
  h: '66%',
  p: '2.5',
  borderRadius: 'l2',
  bg: 'rgba(255, 255, 255, .85)',
  '& i': { display: 'block', borderRadius: '6px', bg: 'paper.200' },
  '& i:nth-child(2)': { bg: 'sky.200' },
  _nebula: {
    gridTemplateRows: '14px 1fr 30px',
    bg: 'bg.glass',
    boxShadow: 'card',
    '& i': { bg: 'brand.subtle' },
    '& i:nth-child(2)': {
      bg: 'radial-gradient(60% 80% at 70% 30%, rgba(124, 92, 255, .5), transparent), {colors.brand.subtle}',
    },
  },
});

const wheel = css({
  position: 'absolute',
  inset: '0',
  bg: 'conic-gradient(from 90deg, {colors.tomato.500}, {colors.sun.400}, {colors.leaf.500}, {colors.cobalt.500}, {colors.blush.300}, {colors.tomato.500})',
  _nebula: {
    bg: 'conic-gradient(from 90deg, {colors.violet.500}, {colors.aurora.magenta}, {colors.aurora.peach}, {colors.aurora.cyan}, {colors.aurora.blue}, {colors.violet.500})',
  },
});

const forever = (duration: number, delay = 0) => ({
  duration,
  delay,
  repeat: Infinity,
  ease: 'easeInOut' as const,
});

export function ShowcaseVisual({
  visual,
  image,
  alt = '',
  className,
}: {
  visual: Visual;
  image?: string;
  alt?: string;
  className?: string;
}) {
  const still = useReducedMotion();

  return (
    <div className={cx(frame, className)} aria-hidden={visual !== 'image'}>
      {visual === 'image' && image && (
        <Image src={image} alt={alt} fill sizes='(min-width: 992px) 40vw, 100vw' />
      )}

      {visual === 'audio' && (
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2',
            h: 'full',
            w: 'full',
            _nebula: { gap: '1.5' },
          })}
        >
          {Array.from({ length: 11 }, (_, i) => (
            <m.i
              key={i}
              className={bar}
              initial={{ height: '22%' }}
              animate={still ? undefined : { height: ['22%', '64%', '22%'] }}
              transition={forever(1.15, -((i * 0.37) % 1.1))}
            />
          ))}
        </div>
      )}

      {visual === 'border' && (
        <>
          <m.div
            className={ring}
            animate={still ? undefined : { rotate: 360 }}
            transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
          />
          <span
            className={css({
              position: 'relative',
              textStyle: 'h3',
              fontFamily: 'display',
              fontWeight: 'display',
            })}
          >
            Fancy
          </span>
        </>
      )}

      {visual === 'dashes' && (
        <svg viewBox='0 0 200 120' className={css({ w: '80%', h: '70%' })}>
          {[
            'M10 100 C 60 100, 60 20, 110 20 S 170 60, 190 30',
            'M10 60 C 50 20, 90 110, 140 80 S 180 90, 190 100',
          ].map((d, i) => (
            <m.path
              key={d}
              d={d}
              className={cx(
                dash,
                i === 1 &&
                  css({ opacity: '0.55', _nebula: { stroke: 'aurora.cyan', opacity: '1' } }),
              )}
              animate={still ? undefined : { strokeDashoffset: [0, -32] }}
              transition={{ duration: i ? 2.2 : 1.4, repeat: Infinity, ease: 'linear' }}
            />
          ))}
        </svg>
      )}

      {visual === 'form' && (
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '2.5',
            w: '64%',
            _nebula: { gap: '2' },
          })}
        >
          {Array.from({ length: 5 }, (_, i) => (
            <i key={i} className={formRow} />
          ))}
        </div>
      )}

      {visual === 'colors' && (
        <>
          <m.div
            className={wheel}
            animate={still ? undefined : { rotate: 360 }}
            transition={{ duration: 24, repeat: Infinity, ease: 'linear' }}
            style={{ scale: 1.6 }}
          />
          <span
            className={css({
              position: 'relative',
              w: '34%',
              aspectRatio: '1',
              borderRadius: 'full',
              bg: 'bg.canvas',
              boxShadow: '0 0 30px {colors.glow}',
              _storybook: { display: 'none' },
            })}
          />
        </>
      )}

      {visual === 'base64' && (
        <span
          className={css({
            alignSelf: 'start',
            justifySelf: 'start',
            p: '3.5',
            textStyle: 'micro',
            wordBreak: 'break-all',
            color: 'sun.400',
            _nebula: { color: 'fg.accent' },
          })}
        >
          data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAy…
        </span>
      )}

      {visual === 'site' && (
        <div className={siteBox}>
          <i />
          <i />
          <i />
        </div>
      )}
    </div>
  );
}
